import { StatusPill } from '@/components/molecules/StatusPill'
import type { FieldDeviceKind, RobotMediaItem } from '@/services/api/domains/robots.api'
import type { RobotAlertItem } from '@/services/api/robot-alerts-store'
import { sosAlertIsMissed, sosIsTimedOut } from '@/services/api/sos-alert-timeout'
import { formatRelativeTime } from '@/utils/formatRelativeTime'
import { useTranslation } from 'react-i18next'

import type { DashboardBridge } from '../Dashboard/DashboardBridge'
import { formatClockTime } from './alert-display'
import { formatLogDay, type AlarmEntry } from './alert-log'
import { severityToPill } from './alert-severity'
import { AlertSnapshot } from './AlertSnapshot'
import { LogLocation } from './LogLocation'
import { PictureThumbButton } from './PictureThumbButton'

interface AlertLogRowProps {
  entry: AlarmEntry
  /** Operator-facing label; null when neither the alert nor the roster knows one. */
  label: string | null
  /** Device class, for the badge. Null when nothing knows — never guessed. */
  kind: FieldDeviceKind | null
  bridge: DashboardBridge | null
  /** Offered on the newest occurrence of an alert that has a snapshot; absent → no button. */
  onEnroll?: (alert: RobotAlertItem) => void
  /** Opens the occurrence's photo full-size; absent → the photo is shown but not clickable. */
  onOpenPicture?: (media: RobotMediaItem) => void
  /** Lists or hides an alert's older occurrences; absent → the count is shown without the toggle. */
  onToggleRepeats?: (alertId: string) => void
}

/**
 * One occurrence of one alarm, as a log line: when (time column), what (pill, name, message),
 * where (location line). No Acknowledge, no Resolve — the row records, it does not dismiss.
 */
export function AlertLogRow({
  entry,
  label,
  kind,
  bridge,
  onEnroll,
  onOpenPicture,
  onToggleRepeats,
}: AlertLogRowProps) {
  const { t } = useTranslation('console')
  const { alert, at, isNewest, repeats } = entry
  // Read at render, which is enough: the log re-renders on every store change and on the element's
  // refresh tick, and being a minute late to call an alarm quiet costs nothing.
  const now = Date.now()
  const timedOut = sosIsTimedOut(alert, now)
  // A critical alarm nobody answered in its ring window. The stored stamp outlives the handling, so
  // the log still says "missed" after someone acknowledges it; an open one is aged here too. One an
  // operator declined was answered, so it is never "missed" — even if the stamp beat the decline.
  const missed =
    alert.severity === 'critical' && !alert.declinedAt && (Boolean(alert.missedAt) || sosAlertIsMissed(alert, now))
  // Never the raw ObjectId. An id is not a name; the id stays reachable as a tooltip.
  const title = label ?? t('alerts.sos.unknownDevice', 'Unknown device')

  return (
    <li className="flex items-start gap-x-3 p-3">
      <div className="w-14 shrink-0 text-right">
        <span className="font-mono text-[11px] text-gray-700 dark:text-gray-300">{formatClockTime(at)}</span>
        <span className="block text-[10px] text-gray-400 dark:text-gray-500">{formatLogDay(at)}</span>
      </div>

      {/* The slot is always rendered so the rows line up whether or not an occurrence has a photo.
          The photo is THIS occurrence's own (an AI alarm names the picture it was raised for), so a
          repeat shows its own frame, not the newest one. When the fetched media holds that picture
          it is the same thumbnail button a "Picture taken" row uses; otherwise (fleet scope, or a
          picture outside the media page) the key is resolved directly. */}
      <div className="h-12 w-12 shrink-0" aria-hidden={!entry.photoRef}>
        {entry.photo && onOpenPicture ? (
          <PictureThumbButton media={entry.photo} onOpen={onOpenPicture} />
        ) : (
          entry.photoRef && (
            <AlertSnapshot
              refKey={entry.photoRef}
              robotId={alert.robotId}
              alt={`${title} snapshot`}
              className="h-12 w-12 rounded object-cover ring-1 ring-gray-200 dark:ring-white/10"
            />
          )
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <StatusPill status={severityToPill(alert.severity)} size="sm" />
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100" title={alert.robotId}>
            {title}
          </span>
          {kind === 'helmet' && (
            <span className="shrink-0 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
              {t('alerts.log.helmet', 'Helmet')}
            </span>
          )}
          {kind === 'tracker' && (
            <span className="shrink-0 rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">
              {t('alerts.log.tracker', 'Tracker')}
            </span>
          )}
          {/* The one tag a log needs that an inbox did not: a handled row stays, so it must say so. */}
          {alert.status !== 'open' && (
            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 dark:bg-white/10 dark:text-gray-300">
              {alert.status === 'acknowledged'
                ? t('alerts.status.acknowledged', 'Acknowledged')
                : t('alerts.status.resolved', 'Resolved')}
            </span>
          )}
          {/* An OPEN alarm that has stopped being reported.
              This is where a timed-out emergency goes: it no longer opens a card, so without a word
              here it would simply have vanished, and "it disappeared" is the worst thing an alert
              surface can do. It is DERIVED, not a status the backend knows — nobody has resolved
              this, the helmet has just gone quiet — so it is worded as an observation rather than
              as an outcome, and only ever shown on the newest sighting of a still-open alarm. */}
          {isNewest && missed && (
            <span
              data-testid="alert-missed"
              className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
            >
              {t('alerts.log.missed', 'Missed')}
            </span>
          )}
          {isNewest && alert.status === 'open' && timedOut && !missed && (
            <span
              data-testid="alert-timed-out"
              title={t(
                'alerts.log.timedOutHint',
                'This alarm has not been reported for over five minutes, so it no longer interrupts. It is still open.',
              )}
              className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
            >
              {t('alerts.log.timedOut', 'No longer reporting')}
            </span>
          )}
        </div>
        {alert.message && (
          <p className="mt-0.5 break-words text-sm text-gray-600 dark:text-gray-300">{alert.message}</p>
        )}

        <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-400 dark:text-gray-500">
          <span>{formatRelativeTime(at)}</span>
          {repeats && (
            <>
              <span aria-hidden>·</span>
              <span>
                {t('alerts.log.repeatsSince', '{{count}}× since {{time}}', {
                  count: repeats.count,
                  time: formatClockTime(repeats.firstAt),
                })}
              </span>
              {onToggleRepeats && (
                <button
                  type="button"
                  aria-expanded={repeats.expanded}
                  onClick={() => onToggleRepeats(alert.alertId)}
                  className="font-medium text-sky-600 hover:underline dark:text-sky-400"
                >
                  {repeats.expanded
                    ? t('alerts.log.hideRepeats', 'Hide repeats')
                    : t('alerts.log.showRepeats', 'Show all')}
                </button>
              )}
            </>
          )}
          <span aria-hidden>·</span>
          <LogLocation lng={alert.lng} lat={alert.lat} bridge={bridge} latestOnly={!isNewest} />
          {onEnroll && isNewest && alert.snapshotRef && (
            <>
              <span aria-hidden>·</span>
              <button
                type="button"
                onClick={() => onEnroll(alert)}
                className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {t('alerts.actions.enroll', 'Enroll')}
              </button>
            </>
          )}
        </p>
      </div>
    </li>
  )
}
